import {
  Bell,
  ChevronRight,
  Home,
  LogOut,
  Moon,
  PanelLeftClose,
  PanelLeftOpen,
  Search,
  Settings,
  Store,
  Sun,
  UserCog,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { ensureLanguageLoaded, staticLanguageOptions } from '../../../shared/i18n';
import { useShop } from '../../../shared/store';

export interface DashboardNavItem {
  id: string;
  label: string;
  to: string;
  icon: LucideIcon;
}

interface DashboardFrameProps {
  brand: string;
  roleLabel: string;
  userName: string;
  userImageUrl?: string;
  pageTitle: string;
  pageDescription: string;
  navItems: DashboardNavItem[];
  activeNavId: string;
  children: ReactNode;
  rightPanel?: ReactNode;
  theme?: 'light' | 'night';
  headerAction?: ReactNode;
  headerVariant?: 'default' | 'admin' | 'buyer';
  showNotifications?: boolean;
  showSettings?: boolean;
  sidebarTogglePlacement?: 'header' | 'sidebar';
}

const themeClassNames = {
  light: {
    shell: 'haapa-dashboard-shell border-slate-200/80 bg-[#f5f6fb] shadow-[0_36px_90px_-65px_rgba(15,23,42,0.48)]',
    sidebar: 'border-r border-slate-800/35 bg-[#111a29] text-slate-100',
    title: 'text-slate-900',
    muted: 'text-slate-500',
    sidebarMuted: 'text-slate-400',
    navIdle: 'text-slate-300 hover:bg-white/10 hover:text-white',
    navActive: 'bg-orange-500 text-white',
    search: 'border-slate-200 bg-white text-slate-700 placeholder:text-slate-400',
    iconButton: 'border-slate-200 bg-white text-slate-600 hover:bg-orange-50 hover:text-orange-700',
    rightPanel: 'border-l border-slate-200 bg-[#f8f9fc]',
    mobileNav: 'border border-slate-200/80 bg-white/95',
    topHeader: 'border-b border-slate-200 bg-[#f5f6fb]/95 backdrop-blur',
    profileMenu: 'border border-slate-200 bg-white',
    profileMenuItem: 'text-slate-700 hover:bg-orange-50 hover:text-orange-700',
    supportCard: 'border border-white/15 bg-white/5 text-slate-200',
  },
  night: {
    shell: 'haapa-dashboard-shell border-slate-700/70 bg-[#080f1f] shadow-[0_36px_90px_-65px_rgba(2,6,23,0.92)]',
    sidebar: 'border-r border-slate-700/70 bg-[#0b1322] text-slate-100',
    title: 'text-slate-100',
    muted: 'text-slate-300',
    sidebarMuted: 'text-slate-500',
    navIdle: 'text-slate-300 hover:bg-slate-800 hover:text-orange-200',
    navActive: 'bg-orange-500 text-white',
    search: 'border-slate-700 bg-slate-900 text-slate-100 placeholder:text-slate-400',
    iconButton: 'border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800 hover:text-orange-200',
    rightPanel: 'border-l border-slate-700/70 bg-[#0b1322]',
    mobileNav: 'border border-slate-700/70 bg-[#0f172a]',
    topHeader: 'border-b border-slate-700/70 bg-[#080f1f]/95 backdrop-blur',
    profileMenu: 'border border-slate-700 bg-[#0f172a]',
    profileMenuItem: 'text-slate-200 hover:bg-slate-800 hover:text-orange-200',
    supportCard: 'border border-slate-700/70 bg-slate-900/40 text-slate-300',
  },
} as const;

const DashboardFrame = ({
  brand,
  roleLabel,
  userName,
  userImageUrl,
  pageTitle,
  pageDescription,
  navItems,
  activeNavId,
  children,
  rightPanel,
  theme = 'light',
  headerAction,
  headerVariant = 'default',
  showNotifications = true,
  showSettings = true,
  sidebarTogglePlacement = 'header',
}: DashboardFrameProps) => {
  const { t, i18n } = useTranslation();
  const { currentUser, logoutAsync, isLoading, themeMode, toggleTheme } = useShop();
  const resolvedTheme = themeMode === 'dark' ? 'night' : theme;
  const styles = themeClassNames[resolvedTheme];
  const hasRightPanel = Boolean(rightPanel);
  const isAdminHeader = headerVariant === 'admin';
  const isBuyerHeader = headerVariant === 'buyer';
  const isCenteredSearchHeader = isAdminHeader || isBuyerHeader;
  const shouldShowHeaderSidebarToggle = sidebarTogglePlacement === 'header';
  const navigate = useNavigate();
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isLanguageLoading, setIsLanguageLoading] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const profileMenuRef = useRef<HTMLDivElement | null>(null);
  const languageCode = `${i18n.resolvedLanguage ?? i18n.language ?? 'en'}`.toLowerCase();
  const currentLanguage = staticLanguageOptions.some((option) => option.code === languageCode)
    ? languageCode
    : 'en';
  const effectiveUserImage = userImageUrl ?? currentUser?.profileImageUrl;
  const normalizedUserName = `${userName ?? ''}`.trim();
  const safeUserName = normalizedUserName || currentUser?.name?.trim() || 'User';
  const userInitials = safeUserName
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((chunk) => chunk.charAt(0).toUpperCase())
    .join('');
  const navPivot = Math.max(3, Math.ceil(navItems.length * 0.55));
  const primaryNavItems = navItems.slice(0, navPivot);
  const secondaryNavItems = navItems.slice(navPivot);

  const handleLogout = async () => {
    setIsLoggingOut(true);
    await logoutAsync();
    setIsLoggingOut(false);
    setIsProfileMenuOpen(false);
    void navigate('/');
  };

  const handleOpenProfile = () => {
    setIsProfileMenuOpen(false);
    void navigate('/profile');
  };

  const changeLanguage = async (languageCode: string) => {
    const normalizedLanguageCode = languageCode.trim().toLowerCase();
    if (!normalizedLanguageCode) {
      return;
    }

    setIsLanguageLoading(true);
    try {
      await ensureLanguageLoaded(normalizedLanguageCode);
      await i18n.changeLanguage(normalizedLanguageCode);
    } finally {
      setIsLanguageLoading(false);
    }
  };

  useEffect(() => {
    if (!isProfileMenuOpen) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target as Node)) {
        setIsProfileMenuOpen(false);
      }
    };

    window.addEventListener('mousedown', handleClickOutside);
    return () => window.removeEventListener('mousedown', handleClickOutside);
  }, [isProfileMenuOpen]);

  const searchField = (
    <label
      className={
        isCenteredSearchHeader
          ? 'relative block w-full max-w-[520px]'
          : 'relative order-last block w-full min-w-[180px] sm:w-[260px] lg:order-none lg:min-w-[260px]'
      }
    >
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 opacity-70" />
      <input
        type="search"
        placeholder={t('dashboardFrame.searchPlaceholder')}
        className={`w-full rounded-xl border py-2.5 pl-10 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-orange-200 ${styles.search}`}
      />
    </label>
  );

  const headerSidebarToggleButton = shouldShowHeaderSidebarToggle ? (
    <button
      type="button"
      onClick={() => setIsSidebarOpen((isOpen) => !isOpen)}
      className={`rounded-xl border p-2 ${styles.iconButton}`}
      aria-label={isSidebarOpen ? t('dashboardFrame.hideSidebar') : t('dashboardFrame.showSidebar')}
    >
      {isSidebarOpen ? <PanelLeftClose className="h-4 w-4" /> : <PanelLeftOpen className="h-4 w-4" />}
    </button>
  ) : null;

  return (
    <section className="-mx-4 sm:-mx-6 lg:-mx-8">
      <div
        className={`h-[calc(100dvh-1rem)] overflow-hidden rounded-none border-x-0 sm:h-[calc(100dvh-2rem)] sm:rounded-[26px] sm:border ${styles.shell}`}
      >
        <div className="flex h-full min-h-0">
          <aside
            className={`${isSidebarOpen ? 'w-[268px] px-5' : 'w-[72px] px-3'} hidden flex-col overflow-y-auto py-6 transition-all duration-300 md:flex ${styles.sidebar}`}
          >
            <div className="flex items-center justify-between gap-2">
              {isSidebarOpen ? (
                <>
                  <div className="flex items-center gap-2">
                    <div className="grid h-9 w-9 place-items-center rounded-xl bg-orange-500 text-white shadow-[0_14px_25px_-16px_rgba(249,115,22,0.9)]">
                      <Store className="h-4 w-4" />
                    </div>
                    <div className="min-w-0">
                      <p className="truncate text-lg font-semibold leading-tight text-white">{brand}</p>
                      <p className={`truncate text-[11px] font-medium uppercase tracking-[0.14em] ${styles.sidebarMuted}`}>{roleLabel}</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setIsSidebarOpen((isOpen) => !isOpen)}
                    className="rounded-lg border border-white/15 bg-white/5 p-1.5 text-slate-300 transition hover:bg-white/10 hover:text-white"
                    aria-label={t('dashboardFrame.hideSidebar')}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </>
              ) : (
                <div className="flex flex-col items-center gap-2">
                  <div className="grid h-9 w-9 place-items-center rounded-xl bg-orange-500 text-white shadow-[0_14px_25px_-16px_rgba(249,115,22,0.9)]">
                    <Store className="h-4 w-4" />
                  </div>
                  <button
                    type="button"
                    onClick={() => setIsSidebarOpen(true)}
                    className="rounded-lg border border-white/15 bg-white/5 p-1.5 text-slate-300 transition hover:bg-white/10 hover:text-white"
                    aria-label={t('dashboardFrame.showSidebar')}
                  >
                    <PanelLeftOpen className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>

            <nav className="mt-8 space-y-6">
              <div>
                {isSidebarOpen && <p className={`mb-2 px-2 text-[11px] font-semibold uppercase tracking-[0.2em] ${styles.sidebarMuted}`}>General</p>}
                <div className="space-y-1.5">
                  {primaryNavItems.map((item) => {
                    const Icon = item.icon;
                    const isActive = item.id === activeNavId;
                    return (
                      <Link
                        key={item.id}
                        to={item.to}
                        className={`flex items-center ${isSidebarOpen ? 'gap-3 px-3' : 'justify-center px-2'} rounded-xl py-2.5 text-sm font-semibold transition ${
                          isActive ? styles.navActive : styles.navIdle
                        }`}
                        title={!isSidebarOpen ? item.label : undefined}
                      >
                        <Icon className="h-4 w-4" />
                        {isSidebarOpen && <span className="line-clamp-1 capitalize">{item.label}</span>}
                      </Link>
                    );
                  })}
                </div>
              </div>

              {secondaryNavItems.length > 0 && (
                <div>
                  {isSidebarOpen && <p className={`mb-2 px-2 text-[11px] font-semibold uppercase tracking-[0.2em] ${styles.sidebarMuted}`}>Workspace</p>}
                  <div className="space-y-1.5">
                    {secondaryNavItems.map((item) => {
                      const Icon = item.icon;
                      const isActive = item.id === activeNavId;
                      return (
                        <Link
                          key={item.id}
                          to={item.to}
                          className={`flex items-center ${isSidebarOpen ? 'gap-3 px-3' : 'justify-center px-2'} rounded-xl py-2.5 text-sm font-semibold transition ${
                            isActive ? styles.navActive : styles.navIdle
                          }`}
                          title={!isSidebarOpen ? item.label : undefined}
                        >
                          <Icon className="h-4 w-4" />
                          {isSidebarOpen && <span className="line-clamp-1 capitalize">{item.label}</span>}
                        </Link>
                      );
                    })}
                  </div>
                </div>
              )}
            </nav>

            {isSidebarOpen && (
              <div className={`mt-auto rounded-2xl p-4 ${styles.supportCard}`}>
                <p className="text-xs font-semibold uppercase tracking-wide text-orange-200">{t('dashboardFrame.supportTitle')}</p>
                <p className="mt-2 text-xs leading-relaxed opacity-80">
                  {t('dashboardFrame.supportDescription')}
                </p>
              </div>
            )}
          </aside>

          <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
            <header className={`shrink-0 sticky top-0 z-20 px-4 py-4 sm:px-6 ${styles.topHeader}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className={`text-[11px] font-semibold uppercase tracking-[0.18em] ${styles.muted}`}>{roleLabel}</p>
                  <h1 className={`mt-1 text-2xl font-semibold sm:text-3xl ${styles.title}`}>{pageTitle}</h1>
                  <p className={`mt-1 text-sm ${styles.muted}`}>{pageDescription}</p>
                </div>

                {isCenteredSearchHeader ? (
                  <div className="w-full lg:grid lg:grid-cols-[auto_1fr_auto] lg:items-center lg:gap-3">
                    <div className="flex w-full flex-wrap items-center gap-2 lg:w-auto">
                      {headerAction}
                    </div>
                    <div className="mt-3 flex w-full justify-center lg:mt-0">{searchField}</div>
                    <div className="mt-3 flex w-full flex-wrap items-center justify-end gap-2 lg:mt-0 lg:w-auto">
                      {headerSidebarToggleButton}
                      {!isBuyerHeader ? (
                        <button
                          type="button"
                          onClick={() => void navigate('/')}
                          className={`hidden rounded-xl border p-2 sm:inline-flex ${styles.iconButton}`}
                          aria-label={t('dashboardFrame.goHome')}
                        >
                          <Home className="h-4 w-4" />
                        </button>
                      ) : null}
                      {showNotifications ? (
                        <button
                          type="button"
                          className={`relative rounded-xl border p-2 ${styles.iconButton}`}
                          aria-label="Notifications"
                        >
                          <Bell className="h-4 w-4" />
                          <span className="absolute right-1.5 top-1.5 h-1.5 w-1.5 rounded-full bg-orange-500" />
                        </button>
                      ) : null}
                      {showSettings && !isBuyerHeader ? (
                        <button
                          type="button"
                          className={`hidden rounded-xl border p-2 sm:inline-flex ${styles.iconButton}`}
                          aria-label="Settings"
                        >
                          <Settings className="h-4 w-4" />
                        </button>
                      ) : null}
                      <button
                        type="button"
                        onClick={toggleTheme}
                        className={`rounded-xl border p-2 ${styles.iconButton}`}
                        aria-label="Toggle theme"
                      >
                        {themeMode === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                      </button>
                      <label className="sr-only" htmlFor="dashboard-language-select">
                        {t('language.label')}
                      </label>
                      <select
                        id="dashboard-language-select"
                        value={currentLanguage}
                        onChange={(event) => void changeLanguage(event.target.value)}
                        disabled={isLanguageLoading}
                        className={`max-w-[92px] rounded-xl border px-2 py-2 text-xs font-semibold uppercase tracking-wide [&>option]:bg-white [&>option]:text-slate-900 ${styles.iconButton}`}
                      >
                        {staticLanguageOptions.map((option) => (
                          <option key={option.code} value={option.code} className="bg-white text-slate-900">
                            {option.shortLabel}
                          </option>
                        ))}
                      </select>
                      <div className="relative" ref={profileMenuRef}>
                        <button
                          type="button"
                          onClick={() => setIsProfileMenuOpen((isOpen) => !isOpen)}
                          className={`flex items-center gap-2 rounded-xl border px-2 py-1.5 text-sm ${styles.iconButton}`}
                          aria-expanded={isProfileMenuOpen}
                          aria-haspopup="menu"
                          aria-label={t('dashboardFrame.profileMenu')}
                        >
                          {effectiveUserImage ? (
                            <img src={effectiveUserImage} alt={safeUserName} className="h-8 w-8 rounded-full object-cover" />
                          ) : (
                            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-orange-500 text-xs font-semibold text-white">
                              {userInitials || 'U'}
                            </div>
                          )}
                          <div className="hidden pr-1 text-left leading-tight lg:block">
                            <p className="font-semibold leading-none">{safeUserName}</p>
                            <p className="mt-1 text-xs opacity-75">{roleLabel}</p>
                          </div>
                        </button>

                        {isProfileMenuOpen && (
                          <div
                            role="menu"
                            className={`absolute right-0 z-50 mt-2 w-44 rounded-xl p-1.5 shadow-lg ${styles.profileMenu}`}
                          >
                            <button
                              type="button"
                              onClick={handleOpenProfile}
                              className={`flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm font-semibold transition ${styles.profileMenuItem}`}
                            >
                              <UserCog className="h-4 w-4" />
                              {t('dashboardFrame.editProfile')}
                            </button>
                            <button
                              type="button"
                              onClick={() => void handleLogout()}
                              disabled={isLoading || isLoggingOut}
                              className={`flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm font-semibold transition ${styles.profileMenuItem}`}
                            >
                              <LogOut className="h-4 w-4" />
                              {t('common.logout')}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="ml-auto flex w-full flex-wrap items-center justify-end gap-2 lg:w-auto">
                    {headerAction}
                    {headerSidebarToggleButton}
                  <button
                    type="button"
                    onClick={() => void navigate('/')}
                    className={`hidden rounded-xl border p-2 sm:inline-flex ${styles.iconButton}`}
                    aria-label={t('dashboardFrame.goHome')}
                  >
                    <Home className="h-4 w-4" />
                  </button>
                  {showNotifications ? (
                    <button
                      type="button"
                      className={`relative rounded-xl border p-2 ${styles.iconButton}`}
                      aria-label="Notifications"
                    >
                      <Bell className="h-4 w-4" />
                      <span className="absolute right-1.5 top-1.5 h-1.5 w-1.5 rounded-full bg-orange-500" />
                    </button>
                  ) : null}
                  {showSettings ? (
                    <button
                      type="button"
                      className={`hidden rounded-xl border p-2 sm:inline-flex ${styles.iconButton}`}
                      aria-label="Settings"
                    >
                      <Settings className="h-4 w-4" />
                    </button>
                  ) : null}
                  <button
                    type="button"
                    onClick={toggleTheme}
                    className={`rounded-xl border p-2 ${styles.iconButton}`}
                    aria-label="Toggle theme"
                  >
                    {themeMode === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                  </button>
                  <label className="sr-only" htmlFor="dashboard-language-select">
                    {t('language.label')}
                  </label>
                  <select
                    id="dashboard-language-select"
                    value={currentLanguage}
                    onChange={(event) => void changeLanguage(event.target.value)}
                    disabled={isLanguageLoading}
                    className={`max-w-[92px] rounded-xl border px-2 py-2 text-xs font-semibold uppercase tracking-wide [&>option]:bg-white [&>option]:text-slate-900 ${styles.iconButton}`}
                  >
                    {staticLanguageOptions.map((option) => (
                      <option key={option.code} value={option.code} className="bg-white text-slate-900">
                        {option.shortLabel}
                      </option>
                    ))}
                  </select>
                  <div className="relative" ref={profileMenuRef}>
                    <button
                      type="button"
                      onClick={() => setIsProfileMenuOpen((isOpen) => !isOpen)}
                      className={`flex items-center gap-2 rounded-xl border px-2 py-1.5 text-sm ${styles.iconButton}`}
                      aria-expanded={isProfileMenuOpen}
                      aria-haspopup="menu"
                      aria-label={t('dashboardFrame.profileMenu')}
                    >
                      {effectiveUserImage ? (
                        <img src={effectiveUserImage} alt={safeUserName} className="h-8 w-8 rounded-full object-cover" />
                      ) : (
                        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-orange-500 text-xs font-semibold text-white">
                          {userInitials || 'U'}
                        </div>
                      )}
                      <div className="hidden pr-1 text-left leading-tight lg:block">
                        <p className="font-semibold leading-none">{safeUserName}</p>
                        <p className="mt-1 text-xs opacity-75">{roleLabel}</p>
                      </div>
                    </button>

                    {isProfileMenuOpen && (
                      <div
                        role="menu"
                        className={`absolute right-0 z-50 mt-2 w-44 rounded-xl p-1.5 shadow-lg ${styles.profileMenu}`}
                      >
                        <button
                          type="button"
                          onClick={handleOpenProfile}
                          className={`flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm font-semibold transition ${styles.profileMenuItem}`}
                        >
                          <UserCog className="h-4 w-4" />
                          {t('dashboardFrame.editProfile')}
                        </button>
                        <button
                          type="button"
                          onClick={() => void handleLogout()}
                          disabled={isLoading || isLoggingOut}
                          className={`flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm font-semibold transition ${styles.profileMenuItem}`}
                        >
                          <LogOut className="h-4 w-4" />
                          {t('common.logout')}
                        </button>
                      </div>
                    )}
                  </div>
                    {searchField}
                  </div>
                )}
              </div>

              <div className={`mt-4 flex gap-2 overflow-x-auto rounded-xl p-2 lg:hidden ${styles.mobileNav}`}>
                {navItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = item.id === activeNavId;
                  return (
                    <Link
                      key={item.id}
                      to={item.to}
                      className={`inline-flex shrink-0 items-center gap-2 rounded-lg px-3 py-2 text-xs font-semibold transition ${
                        isActive ? styles.navActive : styles.navIdle
                      }`}
                    >
                      <Icon className="h-3.5 w-3.5" />
                      {item.label}
                    </Link>
                  );
                })}
              </div>
            </header>

            <div className={`flex w-full min-h-0 flex-1 overflow-hidden`}>
              <div className="haapa-dashboard-content w-full flex-1 min-h-0 min-w-0 overflow-y-auto px-4 pb-4 pt-4 sm:px-6 sm:pb-6">
                {children}
              </div>
              {hasRightPanel && (
                <aside className={`haapa-dashboard-sidepanel hidden xl:block w-[320px] shrink-0 min-h-0 overflow-y-auto border-l px-4 py-4 sm:px-5 ${styles.rightPanel}`}>
                  <div className="xl:sticky xl:top-24">{rightPanel}</div>
                </aside>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DashboardFrame;
